import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import {
  SUBSCRIPTION_INTERVALS,
  SUBSCRIPTION_INTERVAL_LABELS,
  SUBSCRIPTION_INTERVAL_MONTHS,
  SUBSCRIPTION_KIND_LABELS,
  ISO_MONTH,
  addMonthsToMonth,
  applicationOperationsMonthLabel,
  canOpenContract,
  compareMonths,
  formatCurrency,
  formatNumber,
  nextSubscriptionPeriod,
  parseNumber,
  subscriptionPeriodAmount,
  subscriptionPeriodLabel,
  sumMoney,
  type CreateSubscriptionRequest,
  type OverwriteSubscriptionRequest,
  type Paginated,
  type SubscriptionContractsResponse,
  type SubscriptionInterval,
  type SubscriptionKind,
  type SubscriptionOverview,
  type SubscriptionRow,
} from '@codepass/shared';
import { ApiError, api } from '@/lib/api';
import { useAuth } from '@/auth/AuthProvider';
import { contractPath, customerPath } from '@/lib/paths';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardHeader,
  CardTitle,
  Combobox,
  Input,
  Label,
  Modal,
  Select,
  Skeleton,
  Table,
  Td,
  Th,
  Tr,
} from '@/components/ui';
import {
  EmptyState,
  Money,
  PageHeader,
  Pagination,
  PermittedLink,
  RefreshButton,
} from '@/components/domain';
import { SubNav } from '@/pages/Invoices';
import type { CustomerListItem } from '@/types';

/**
 * /fakturering/abonnementer - every customer with application operations or an app.
 *
 * Two kinds of row: the `Applikations drift` and `App` lines on won contracts in CRM, billed every
 * month, and the subscriptions created here with their own price and interval. The invoices pick
 * up both - see subscription.ts in @codepass/shared. Everything shown is worked out by the server;
 * the dialogs only preview the first invoice with the same shared functions.
 *
 * **Not optimistic.** A subscription puts money on a customer's invoices; the dialog waits for the
 * answer, so a refusal - a second subscription on the same product, an item gone from BC - is
 * shown where it was made.
 */

const QUERY_KEY = ['subscriptions'];

/**
 * Rows per page - as on /fakturering. Paged here and not by the server: the list is built of CRM
 * and Codepass together and is short, so the search runs over all of it.
 */
const PAGE_SIZE = 50;

function errorText(error: unknown, fallback: string): string {
  return error instanceof ApiError ? error.message : fallback;
}

/** `november 2026`, or the em dash for none. */
function monthLabel(month: string | null): string {
  return month ? applicationOperationsMonthLabel(month) : '—';
}

/** The words a row can be found by. */
function searchText(row: SubscriptionRow): string {
  return [
    row.customer.name,
    row.product,
    row.contract?.contract_no,
    row.contract?.title,
    SUBSCRIPTION_KIND_LABELS[row.kind],
    SUBSCRIPTION_INTERVAL_LABELS[row.interval],
    row.source === 'crm' ? 'crm' : 'codepass',
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
}

/** Still billed: a next period to come. Ended and replaced rows are not. */
function isRunning(row: SubscriptionRow): boolean {
  return row.next !== null;
}

export function SubscriptionsPage() {
  /* 'Se abonnementsoversigt' uden 'Opret og afslut abonnementer': listen uden Opret og Afslut. */
  const canManage = useAuth().can('subscriptions.manage');
  const [search, setSearch] = useState('');
  const [offset, setOffset] = useState(0);
  const [creating, setCreating] = useState(false);
  const [ending, setEnding] = useState<SubscriptionRow | null>(null);
  const [overwriting, setOverwriting] = useState<SubscriptionRow | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const [result, setResult] = useState<string | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: () => api.get<SubscriptionOverview>('/subscriptions'),
  });

  const rows = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const all = data?.rows ?? [];
    if (!needle) return all;
    const words = needle.split(/\s+/);
    return all.filter((row) => {
      const text = searchText(row);
      return words.every((word) => text.includes(word));
    });
  }, [data, search]);

  /*
   * Ended, cancelled and overwritten rows go to the archive below. The summary counts every
   * running row the search matches, not only the page.
   */
  const running = rows.filter(isRunning);
  const archived = rows.filter((row) => !isRunning(row));
  const monthly = sumMoney(running.map((row) => row.monthly_excl_vat ?? 0));

  /*
   * Kept on a page that exists: the list can get shorter under the reader - a refresh, or an
   * ended subscription - and a page past the end would show an empty table.
   */
  const lastPage =
    running.length === 0 ? 0 : Math.floor((running.length - 1) / PAGE_SIZE) * PAGE_SIZE;
  const pageOffset = Math.min(offset, lastPage);
  const pageRows = running.slice(pageOffset, pageOffset + PAGE_SIZE);

  return (
    <>
      <PageHeader
        title="Abonnementsoversigt"
        description="Kunder med applikationsdrift eller en app: fra aftalerne i CRM, og dem oprettet her. De faktureres forud på kladden, der fakturerer deres måned."
        actions={
          <>
            <RefreshButton prefixes={['subscriptions']} />
            {canManage ? (
              <Button
                size="sm"
                disabled={!data?.ready || (data?.items.length ?? 0) === 0}
                onClick={() => setCreating(true)}
              >
                <Plus className="h-3.5 w-3.5" aria-hidden />
                Opret abonnement
              </Button>
            ) : null}
          </>
        }
      />
      <SubNav />

      {result ? (
        <Alert tone="success" className="mb-4" onDismiss={() => setResult(null)}>
          {result}
        </Alert>
      ) : null}

      {isLoading ? (
        <Skeleton className="h-64" />
      ) : error || !data ? (
        <Alert tone="error">{errorText(error, 'Kunne ikke hente abonnementerne.')}</Alert>
      ) : (
        <div className="space-y-4">
          {!data.ready ? (
            <Alert tone="warning" title="Tabellen til abonnementer er ikke sat op">
              Kør SQL’en i docs/subscriptions-setup.md i Supabase’s SQL-editor. Indtil da vises kun
              applikationsdrift og apps fra CRM, og der kan ikke oprettes abonnementer.
            </Alert>
          ) : null}
          {data.notes.map((note) => (
            <Alert key={note} tone="warning">
              {note}
            </Alert>
          ))}

          <Card>
            <div className="flex flex-wrap items-center gap-3 border-b border-line p-3">
              <Input
                className="w-full sm:max-w-sm"
                placeholder="Søg på kunde, produkt eller kontrakt"
                aria-label="Søg i abonnementerne"
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value);
                  /* A new search starts on the first page. */
                  setOffset(0);
                }}
              />
              <span className="text-xs text-ink-muted sm:ml-auto">
                {running.length === 1
                  ? '1 aktivt abonnement'
                  : `${running.length} aktive abonnementer`}
                {' · '}
                {formatCurrency(monthly)} pr. måned
              </span>
            </div>

            {running.length === 0 ? (
              <EmptyState
                title={
                  search
                    ? 'Ingen aktive abonnementer matcher søgningen'
                    : 'Ingen aktive abonnementer'
                }
                description={
                  search
                    ? 'Prøv et andet navn, et produkt eller et kontraktnummer.'
                    : 'Et abonnement kommer med her, når en vundet aftale i CRM har en linje med "Applikations drift" eller "App", eller når det oprettes med knappen ovenfor.'
                }
              />
            ) : (
              <>
                <SubscriptionsTable
                  rows={pageRows}
                  onEnd={canManage ? setEnding : null}
                  onOverwrite={canManage ? setOverwriting : null}
                />
                <Pagination
                  total={running.length}
                  limit={PAGE_SIZE}
                  offset={pageOffset}
                  onChange={setOffset}
                />
              </>
            )}
          </Card>

          {archived.length > 0 ? (
            <Card>
              <CardHeader className="flex items-center gap-3">
                <CardTitle>Arkiveret</CardTitle>
                <span className="text-xs text-ink-muted">
                  {archived.length === 1 ? '1 abonnement' : `${archived.length} abonnementer`} ·
                  afsluttet, annulleret eller overskrevet
                </span>
                <Button
                  size="sm"
                  variant="ghost"
                  className="ml-auto"
                  aria-expanded={showArchived}
                  onClick={() => setShowArchived((shown) => !shown)}
                >
                  {showArchived ? 'Skjul' : 'Vis'}
                </Button>
              </CardHeader>
              {/* shortcut: not paged - the archive grows by a few rows a year; page it if it gets long. */}
              {showArchived ? (
                <SubscriptionsTable rows={archived} onEnd={null} onOverwrite={null} />
              ) : null}
            </Card>
          ) : null}

          <p className="text-xs text-ink-muted">
            Abonnementerne faktureres forud på den automatiske kladde, der lukker en måned, eller på
            en faktura oprettet på /fakturering/ny den 1. Et kvartal er 3 × prisen pr. måned, et år
            12 ×. Et abonnement oprettet her erstatter CRM-linjen for samme kunde og produkt fra sin
            første måned, så kunden ikke faktureres to gange. Overskriv gemmer nye vilkår som et
            abonnement her fra en valgt måned; den gamle række flyttes til Arkiveret.
          </p>
        </div>
      )}

      {creating && data ? (
        <CreateSubscriptionModal
          overview={data}
          onClose={() => setCreating(false)}
          onCreated={(text) => {
            setCreating(false);
            setResult(text);
          }}
        />
      ) : null}

      {overwriting && data ? (
        <OverwriteSubscriptionModal
          row={overwriting}
          nextMonth={data.next_month}
          onClose={() => setOverwriting(null)}
          onOverwritten={(text) => {
            setOverwriting(null);
            setResult(text);
          }}
        />
      ) : null}

      {ending && data ? (
        <EndSubscriptionModal
          row={ending}
          nextMonth={data.next_month}
          onClose={() => setEnding(null)}
          onEnded={(text) => {
            setEnding(null);
            setResult(text);
          }}
        />
      ) : null}
    </>
  );
}

/**
 * Whether a row can be overwritten: running, not taken over by a subscription already - and a CRM
 * line needs its customer in BC, which the new subscription is saved on.
 */
function canOverwrite(row: SubscriptionRow): boolean {
  if (!row.next) return false;
  return row.source === 'crm'
    ? row.customer.id !== null && row.replaced_from === null
    : row.end_month === null;
}

function SubscriptionsTable({
  rows,
  onEnd,
  onOverwrite,
}: {
  rows: readonly SubscriptionRow[];
  /** Null without 'Opret og afslut abonnementer' - then there is no Afslut. */
  onEnd: ((row: SubscriptionRow) => void) | null;
  /** Null without 'Opret og afslut abonnementer', and in the archive. */
  onOverwrite: ((row: SubscriptionRow) => void) | null;
}) {
  /* The overview does not include the customers or the contracts - they link only with those. */
  const { can, user } = useAuth();
  const opensContract = canOpenContract(user, null);
  return (
    <Table>
      <thead>
        <tr>
          <Th>Kunde</Th>
          <Th>Produkt</Th>
          <Th className="w-[150px]">Fakturering</Th>
          <Th className="w-[130px] text-right">Pr. måned</Th>
          <Th className="w-[210px]">Næste faktura</Th>
          <Th className="w-[180px]" />
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <Tr key={row.key}>
            <Td>
              {row.customer.id ? (
                <PermittedLink
                  to={customerPath({ id: row.customer.id, cvr: row.customer.cvr })}
                  allowed={can('customers.view')}
                  className="font-medium hover:underline"
                >
                  {row.customer.name}
                </PermittedLink>
              ) : (
                <span className="font-medium">{row.customer.name}</span>
              )}
              <span className="mt-0.5 block text-xs text-ink-muted">
                {row.source === 'crm' ? 'CRM' : 'Oprettet i Codepass'}
                {row.contract ? (
                  <>
                    {' · '}
                    <PermittedLink
                      to={contractPath(row.contract)}
                      allowed={opensContract}
                      className="hover:underline"
                    >
                      {row.contract.contract_no ?? row.contract.title}
                    </PermittedLink>
                  </>
                ) : null}
                {row.source === 'codepass' && row.created_by_name
                  ? ` · af ${row.created_by_name}`
                  : ''}
              </span>
            </Td>
            <Td>
              <span>
                {row.quantity !== 1 ? `${formatNumber(row.quantity, 0)} × ` : ''}
                {row.product}
              </span>
              <span className="mt-0.5 block text-xs text-ink-muted">
                {SUBSCRIPTION_KIND_LABELS[row.kind]}
              </span>
            </Td>
            <Td className="text-sm">
              {SUBSCRIPTION_INTERVAL_LABELS[row.interval]}
              <span className="mt-0.5 block text-xs text-ink-muted">
                {row.source === 'crm' ? 'Vundet' : 'Fra'} {monthLabel(row.start_month)}
                {row.end_month ? ` · til og med ${monthLabel(row.end_month)}` : ''}
              </span>
            </Td>
            <Td className="text-right">
              {row.monthly_excl_vat === null ? (
                <span className="text-ink-muted">—</span>
              ) : (
                <Money value={row.monthly_excl_vat} />
              )}
            </Td>
            <Td className="text-sm">
              <NextInvoice row={row} />
            </Td>
            <Td className="whitespace-nowrap text-right">
              {onOverwrite && canOverwrite(row) ? (
                <Button size="sm" variant="ghost" onClick={() => onOverwrite(row)}>
                  Overskriv
                </Button>
              ) : null}
              {onEnd && row.source === 'codepass' && row.end_month === null ? (
                <Button size="sm" variant="ghost" onClick={() => onEnd(row)}>
                  Afslut
                </Button>
              ) : null}
            </Td>
          </Tr>
        ))}
      </tbody>
    </Table>
  );
}

function NextInvoice({ row }: { row: SubscriptionRow }) {
  if (!row.next) {
    if (row.replaced_from) {
      return (
        <span className="text-xs text-ink-muted">
          Erstattet af abonnement fra {monthLabel(row.replaced_from)}
        </span>
      );
    }
    const cancelled =
      row.end_month !== null &&
      row.start_month !== null &&
      compareMonths(row.end_month, row.start_month) < 0;
    return <Badge>{cancelled ? 'Annulleret' : 'Afsluttet'}</Badge>;
  }

  return (
    <>
      {subscriptionPeriodLabel(row.next.month, row.next.months)}
      <span className="mt-0.5 block text-xs text-ink-muted">
        {row.next.amount_excl_vat === null ? (
          'Prisen mangler'
        ) : (
          <>
            <Money value={row.next.amount_excl_vat} />
            {row.next.months > 1 ? ` for ${row.next.months} måneder` : ''}
          </>
        )}
      </span>
      {row.replaced_from ? (
        <span className="mt-0.5 block text-xs text-ink-muted">
          Erstattes af abonnement fra {monthLabel(row.replaced_from)}
        </span>
      ) : null}
      {row.problem ? <span className="mt-1 block text-xs text-danger">{row.problem}</span> : null}
    </>
  );
}

function CreateSubscriptionModal({
  overview,
  onClose,
  onCreated,
}: {
  overview: SubscriptionOverview;
  onClose: () => void;
  onCreated: (text: string) => void;
}) {
  const queryClient = useQueryClient();
  const [customerId, setCustomerId] = useState('');
  /* Optional - empty is no contract. */
  const [contractId, setContractId] = useState('');
  const [kind, setKind] = useState<SubscriptionKind>('application_operations');
  const [itemId, setItemId] = useState('');
  const [priceText, setPriceText] = useState('');
  const [interval, setBillingInterval] = useState<SubscriptionInterval>('monthly');
  const [startMonth, setStartMonth] = useState(overview.next_month);
  const [error, setError] = useState<string | null>(null);

  /* The same list and key as /fakturering/ny, so the two share the answer. */
  const customers = useQuery({
    queryKey: ['customers', 'for-invoicing'],
    queryFn: () => api.get<Paginated<CustomerListItem>>('/customers', { limit: 500 }),
  });

  const customerOptions = useMemo(
    () =>
      (customers.data?.data ?? [])
        .filter((customer) => !customer.archived)
        .map((customer) => ({
          value: customer.id,
          label: customer.bc_customer_no
            ? `${customer.name} (${customer.bc_customer_no})`
            : customer.name,
        })),
    [customers.data],
  );

  /* The chosen customer's won contracts in CRM - the ones the subscription can be bound to. */
  const contracts = useQuery({
    queryKey: ['subscriptions', 'contracts', customerId],
    queryFn: () =>
      api.get<SubscriptionContractsResponse>('/subscriptions/contracts', {
        customer_id: customerId,
      }),
    enabled: Boolean(customerId),
  });
  const contract = contracts.data?.contracts.find((row) => row.id === contractId) ?? null;

  const items = overview.items.filter((item) => item.kind === kind);
  const item = overview.items.find((candidate) => candidate.id === itemId) ?? null;
  const price = parseNumber(priceText);
  const validPrice = Number.isFinite(price) && price > 0;
  const validStart = ISO_MONTH.test(startMonth);

  /* The first invoice, as the server will bill it - the shared functions, not a sum here. */
  const first = validStart
    ? nextSubscriptionPeriod(
        { interval, start_month: startMonth, end_month: null },
        overview.next_month,
      )
    : null;

  /*
   * A CRM line this subscription takes over - say so, rather than surprise anyone. Bound to a
   * contract, it takes over the line on that contract only; without one, on all the customer's.
   * The server decides it the same way - see `crmLineReplacedFrom`.
   */
  const crmLine = item
    ? overview.rows.find(
        (row) =>
          row.source === 'crm' &&
          row.customer.id === customerId &&
          row.product.toLowerCase() === item.name.toLowerCase() &&
          (!contract || row.contract?.id === contract.id),
      )
    : undefined;

  const create = useMutation({
    mutationFn: (body: CreateSubscriptionRequest) =>
      api.post<SubscriptionRow>('/subscriptions', body),
    onSuccess: (row) => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      onCreated(
        `Abonnementet på ${row.product} til ${row.customer.name} er oprettet.` +
          (row.next
            ? ` Første faktura er kladden for ${applicationOperationsMonthLabel(row.next.month)}.`
            : ''),
      );
    },
    onError: (cause) => setError(errorText(cause, 'Kunne ikke oprette abonnementet.')),
  });

  const canSubmit = Boolean(customerId && item && validPrice && validStart) && !create.isPending;

  return (
    <Modal
      open
      onClose={onClose}
      title="Opret abonnement"
      description="Applikationsdrift eller en app, faktureret forud på kundens kladder."
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Annullér
          </Button>
          <Button
            disabled={!canSubmit}
            loading={create.isPending}
            onClick={() => {
              if (!item) return;
              setError(null);
              create.mutate({
                customer_id: customerId,
                contract_id: contract?.id ?? null,
                bc_item_id: item.id,
                monthly_price_excl_vat: price,
                interval,
                start_month: startMonth,
              });
            }}
          >
            Opret
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div>
          <Label>Kunde</Label>
          <Combobox
            value={customerId}
            onChange={(next) => {
              setCustomerId(next);
              /* Another customer's contracts: the chosen one is not among them. */
              setContractId('');
            }}
            options={customerOptions}
            allLabel={customers.isLoading ? 'Henter kunder …' : 'Vælg kunde'}
            searchPlaceholder="Søg kunde"
            disabled={customers.isLoading}
          />
          {customers.error ? (
            <p className="mt-1 text-xs text-danger">
              {errorText(customers.error, 'Kunne ikke hente kunderne.')}
            </p>
          ) : null}
        </div>

        <div>
          <Label htmlFor="subscription-contract">Kontrakt (valgfri)</Label>
          <Select
            id="subscription-contract"
            value={contractId}
            disabled={!customerId || contracts.isLoading}
            onChange={(event) => setContractId(event.target.value)}
          >
            <option value="">
              {!customerId
                ? 'Vælg kunde først'
                : contracts.isLoading
                  ? 'Henter kontrakter …'
                  : 'Ingen kontrakt'}
            </option>
            {(contracts.data?.contracts ?? []).map((option) => (
              <option key={option.id} value={option.id}>
                {option.contract_no ? `${option.contract_no} · ${option.title}` : option.title}
              </option>
            ))}
          </Select>
          {contracts.error ? (
            <p className="mt-1 text-xs text-danger">
              {errorText(contracts.error, 'Kunne ikke hente kundens kontrakter.')}
            </p>
          ) : customerId && contracts.data && contracts.data.contracts.length === 0 ? (
            <p className="mt-1 text-xs text-ink-muted">
              Kunden har ingen vundne aftaler i CRM. Abonnementet oprettes uden kontrakt.
            </p>
          ) : (
            <p className="mt-1 text-xs text-ink-muted">
              Kundens vundne aftaler i CRM. Med en kontrakt står abonnementet på den på fakturaen.
            </p>
          )}
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <Label htmlFor="subscription-kind">Type</Label>
            <Select
              id="subscription-kind"
              value={kind}
              onChange={(event) => {
                setKind(event.target.value as SubscriptionKind);
                setItemId('');
                setPriceText('');
              }}
            >
              {(Object.keys(SUBSCRIPTION_KIND_LABELS) as SubscriptionKind[]).map((option) => (
                <option key={option} value={option}>
                  {SUBSCRIPTION_KIND_LABELS[option]}
                </option>
              ))}
            </Select>
          </div>
          <div>
            <Label htmlFor="subscription-item">Vare i Business Central</Label>
            <Select
              id="subscription-item"
              value={itemId}
              onChange={(event) => {
                const next = overview.items.find((option) => option.id === event.target.value);
                setItemId(event.target.value);
                if (next) setPriceText(formatNumber(next.unit_price_excl_vat, 2));
              }}
            >
              <option value="">Vælg vare</option>
              {items.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.name}
                </option>
              ))}
            </Select>
            {items.length === 0 ? (
              <p className="mt-1 text-xs text-ink-muted">
                Ingen varer af den type i Business Central. Opret varen dér, og prøv igen.
              </p>
            ) : null}
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-3">
          <div>
            <Label htmlFor="subscription-price">Pris pr. måned (ekskl. moms)</Label>
            <Input
              id="subscription-price"
              inputMode="decimal"
              placeholder="0,00"
              value={priceText}
              onChange={(event) => setPriceText(event.target.value)}
            />
            {item ? (
              <p className="mt-1 text-xs text-ink-muted">
                Varens pris i BC: {formatCurrency(item.unit_price_excl_vat)}
              </p>
            ) : null}
          </div>
          <div>
            <Label htmlFor="subscription-interval">Faktureres</Label>
            <Select
              id="subscription-interval"
              value={interval}
              onChange={(event) => setBillingInterval(event.target.value as SubscriptionInterval)}
            >
              {SUBSCRIPTION_INTERVALS.map((option) => (
                <option key={option} value={option}>
                  {SUBSCRIPTION_INTERVAL_LABELS[option]}
                </option>
              ))}
            </Select>
          </div>
          <div>
            <Label htmlFor="subscription-start">Første måned</Label>
            <Input
              id="subscription-start"
              type="month"
              placeholder="ÅÅÅÅ-MM"
              value={startMonth}
              onChange={(event) => setStartMonth(event.target.value)}
            />
          </div>
        </div>

        {validPrice && first ? (
          <Alert tone="info">
            Første faktura er kladden for {applicationOperationsMonthLabel(first.month)}:{' '}
            {SUBSCRIPTION_INTERVAL_MONTHS[interval] > 1 ? `${first.months} × ` : ''}
            {formatCurrency(price)}
            {first.months > 1
              ? ` = ${formatCurrency(subscriptionPeriodAmount(price, first.months))}`
              : ''}{' '}
            for {subscriptionPeriodLabel(first.month, first.months)}.
            {SUBSCRIPTION_INTERVAL_MONTHS[interval] > 1
              ? ` Derefter hver ${SUBSCRIPTION_INTERVAL_MONTHS[interval]}. måned.`
              : ' Derefter hver måned.'}
          </Alert>
        ) : null}

        {validStart && first && first.month !== startMonth ? (
          <Alert tone="warning">
            Kladden for {applicationOperationsMonthLabel(startMonth)} er allerede lavet. Codepass
            fakturerer ikke bagud, så perioder, der begynder før{' '}
            {applicationOperationsMonthLabel(first.month)}, skal faktureres i hånden.
          </Alert>
        ) : null}

        {crmLine ? (
          <Alert tone="warning">
            Kunden har {crmLine.product} på{' '}
            {crmLine.contract?.contract_no ?? crmLine.contract?.title ?? 'en aftale'} i CRM. Fra{' '}
            {validStart ? applicationOperationsMonthLabel(startMonth) : 'første måned'} faktureres
            det her abonnement i stedet for CRM-linjen, så kunden ikke betaler to gange.
          </Alert>
        ) : null}

        {error ? <Alert tone="error">{error}</Alert> : null}
      </div>
    </Modal>
  );
}

/**
 * "Overskriv": new terms for a running row from a month on, saved as a Codepass subscription - see
 * `overwriteSubscription` in the API. A CRM line is then read from Codepass from that month; a
 * Codepass subscription ends the month before. Same customer, contract and product.
 */
function OverwriteSubscriptionModal({
  row,
  nextMonth,
  onClose,
  onOverwritten,
}: {
  row: SubscriptionRow;
  nextMonth: string;
  onClose: () => void;
  onOverwritten: (text: string) => void;
}) {
  const queryClient = useQueryClient();
  /* The first month not billed yet - the earliest the new terms can start. */
  const earliest = row.next?.month ?? nextMonth;
  const [priceText, setPriceText] = useState(
    row.monthly_excl_vat === null ? '' : formatNumber(row.monthly_excl_vat, 2),
  );
  const [interval, setBillingInterval] = useState<SubscriptionInterval>(row.interval);
  const [startMonth, setStartMonth] = useState(earliest);
  const [error, setError] = useState<string | null>(null);

  const price = parseNumber(priceText);
  const validPrice = Number.isFinite(price) && price > 0;
  const validStart = ISO_MONTH.test(startMonth) && compareMonths(startMonth, earliest) >= 0;
  const first = validStart
    ? nextSubscriptionPeriod({ interval, start_month: startMonth, end_month: null }, startMonth)
    : null;

  const overwrite = useMutation({
    mutationFn: (body: OverwriteSubscriptionRequest) =>
      api.post<SubscriptionRow>('/subscriptions/overwrite', body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      onOverwritten(
        `${row.product} til ${row.customer.name} er overskrevet fra ` +
          `${applicationOperationsMonthLabel(startMonth)}.`,
      );
    },
    onError: (cause) => setError(errorText(cause, 'Kunne ikke overskrive abonnementet.')),
  });

  const id = row.source === 'crm' ? row.key.replace(/^crm:/, '') : row.subscription_id;
  const canSubmit = Boolean(id && validPrice && validStart) && !overwrite.isPending;

  return (
    <Modal
      open
      onClose={onClose}
      title="Overskriv abonnement"
      description={`${row.product} · ${row.customer.name}${
        row.contract ? ` · ${row.contract.contract_no ?? row.contract.title}` : ''
      }`}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Annullér
          </Button>
          <Button
            disabled={!canSubmit}
            loading={overwrite.isPending}
            onClick={() => {
              if (!id) return;
              setError(null);
              overwrite.mutate({
                source: row.source,
                id,
                monthly_price_excl_vat: price,
                interval,
                start_month: startMonth,
              });
            }}
          >
            Overskriv
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-3">
          <div>
            <Label htmlFor="overwrite-price">Pris pr. måned (ekskl. moms)</Label>
            <Input
              id="overwrite-price"
              inputMode="decimal"
              placeholder="0,00"
              value={priceText}
              onChange={(event) => setPriceText(event.target.value)}
            />
            {row.monthly_excl_vat !== null ? (
              <p className="mt-1 text-xs text-ink-muted">
                Nu: {formatCurrency(row.monthly_excl_vat)}
              </p>
            ) : null}
          </div>
          <div>
            <Label htmlFor="overwrite-interval">Faktureres</Label>
            <Select
              id="overwrite-interval"
              value={interval}
              onChange={(event) => setBillingInterval(event.target.value as SubscriptionInterval)}
            >
              {SUBSCRIPTION_INTERVALS.map((option) => (
                <option key={option} value={option}>
                  {SUBSCRIPTION_INTERVAL_LABELS[option]}
                </option>
              ))}
            </Select>
          </div>
          <div>
            <Label htmlFor="overwrite-start">Fra måned</Label>
            <Input
              id="overwrite-start"
              type="month"
              placeholder="ÅÅÅÅ-MM"
              min={earliest}
              value={startMonth}
              onChange={(event) => setStartMonth(event.target.value)}
            />
          </div>
        </div>

        {!validStart ? (
          <p className="text-xs text-danger">
            Vælg {applicationOperationsMonthLabel(earliest)} eller senere. Månederne før er allerede
            faktureret.
          </p>
        ) : null}

        {validPrice && first ? (
          <Alert tone="info">
            Første faktura med de nye vilkår er kladden for{' '}
            {applicationOperationsMonthLabel(first.month)}:{' '}
            {first.months > 1 ? `${first.months} × ` : ''}
            {formatCurrency(price)}
            {first.months > 1
              ? ` = ${formatCurrency(subscriptionPeriodAmount(price, first.months))}`
              : ''}{' '}
            for {subscriptionPeriodLabel(first.month, first.months)}.
          </Alert>
        ) : null}

        {validStart ? (
          <Alert tone="warning">
            {row.source === 'crm'
              ? `Vilkårene gemmes i Codepass. Fra ${applicationOperationsMonthLabel(startMonth)} ` +
                'faktureres abonnementet herfra i stedet for linjen i CRM, også hvis linjen ' +
                'ændres i CRM. CRM røres ikke, og CRM-linjen flyttes til Arkiveret.'
              : `Det nuværende abonnement slutter med ` +
                `${applicationOperationsMonthLabel(addMonthsToMonth(startMonth, -1))} og ` +
                'flyttes til Arkiveret. Perioder, der allerede er faktureret, røres ikke.'}
          </Alert>
        ) : null}

        {error ? <Alert tone="error">{error}</Alert> : null}
      </div>
    </Modal>
  );
}

function EndSubscriptionModal({
  row,
  nextMonth,
  onClose,
  onEnded,
}: {
  row: SubscriptionRow;
  nextMonth: string;
  onClose: () => void;
  onEnded: (text: string) => void;
}) {
  const queryClient = useQueryClient();
  const start = row.start_month ?? nextMonth;
  const earliest = addMonthsToMonth(start, -1);
  /* By default no more invoices: the month before the next period. */
  const suggested = addMonthsToMonth(row.next?.month ?? nextMonth, -1);
  const [endMonth, setEndMonth] = useState(
    compareMonths(suggested, earliest) < 0 ? earliest : suggested,
  );
  const [error, setError] = useState<string | null>(null);

  const valid = ISO_MONTH.test(endMonth) && compareMonths(endMonth, earliest) >= 0;
  const remaining = valid
    ? nextSubscriptionPeriod(
        { interval: row.interval, start_month: start, end_month: endMonth },
        row.next?.month ?? nextMonth,
      )
    : null;

  const end = useMutation({
    mutationFn: () =>
      api.post<SubscriptionRow>(`/subscriptions/${row.subscription_id}/end`, {
        end_month: endMonth,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      onEnded(
        compareMonths(endMonth, start) < 0
          ? `Abonnementet på ${row.product} til ${row.customer.name} er annulleret.`
          : `Abonnementet på ${row.product} til ${row.customer.name} slutter med ` +
              `${applicationOperationsMonthLabel(endMonth)}.`,
      );
    },
    onError: (cause) => setError(errorText(cause, 'Kunne ikke afslutte abonnementet.')),
  });

  return (
    <Modal
      open
      onClose={onClose}
      title="Afslut abonnement"
      description={`${row.product} · ${row.customer.name}`}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Annullér
          </Button>
          <Button
            variant="destructive"
            disabled={!valid || end.isPending}
            loading={end.isPending}
            onClick={() => {
              setError(null);
              end.mutate();
            }}
          >
            Afslut
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div>
          <Label htmlFor="subscription-end">Sidste måned kunden betaler for</Label>
          <Input
            id="subscription-end"
            type="month"
            placeholder="ÅÅÅÅ-MM"
            min={earliest}
            value={endMonth}
            onChange={(event) => setEndMonth(event.target.value)}
          />
        </div>

        {valid ? (
          <Alert tone="info">
            {compareMonths(endMonth, start) < 0
              ? 'Abonnementet bliver aldrig faktureret.'
              : remaining
                ? `Næste faktura er stadig kladden for ` +
                  `${applicationOperationsMonthLabel(remaining.month)}, for ` +
                  `${subscriptionPeriodLabel(remaining.month, remaining.months)}. Den sidste ` +
                  `periode slutter med ${applicationOperationsMonthLabel(endMonth)}.`
                : 'Der kommer ikke flere fakturaer for abonnementet.'}{' '}
            Perioder, der allerede er faktureret, røres ikke.
          </Alert>
        ) : (
          <p className="text-xs text-danger">
            Vælg {applicationOperationsMonthLabel(earliest)} eller senere.
          </p>
        )}

        {error ? <Alert tone="error">{error}</Alert> : null}
      </div>
    </Modal>
  );
}
